@if (formDefinition && form) {
  <!-- Landing Page -->
  @if (submissionFlowState() === 'landing') {
    <app-landing-page
      [title]="landingPageConfig?.title"
      [description]="landingPageConfig?.description"
      [checklistItems]="landingPageChecklistItems"
      [logoUrl]="logoUrl"
      (startSubmission)="onStartSubmission()"
    />
  }

  <!-- Form View -->
  @if (submissionFlowState() === 'form' || submissionFlowState() === 'submitting' || submissionFlowState() === 'uploading' || submissionFlowState() === 'finalizing' || submissionFlowState() === 'error') {
    <div class="box">
      @if (logoUrl && !logoLoadError) {
        <img [src]="logoUrl" alt="Form logo"
             style="max-height: 40px; width: auto;"
             (error)="logoLoadError = true">
      }
      <form [formGroup]="form" (ngSubmit)="onSubmit()">
        @for (field of formDefinition.definition.fields; track field.id) {
          <div class="field">
            <label class="label">{{ field.label }}</label>
            <div class="control">
              @if (field.id) {
                @switch (field.type) {
                  @case ('text') {
                    <input [formControlName]="field.id" [type]="field.type" class="input"
                           [class.is-danger]="form.get(field.id)?.invalid && (form.get(field.id)?.dirty || form.get(field.id)?.touched)">
                  }
                  @case ('textarea') {
                    <textarea [formControlName]="field.id" class="textarea"
                              [class.is-danger]="form.get(field.id)?.invalid && (form.get(field.id)?.dirty || form.get(field.id)?.touched)"></textarea>
                  }
                  @case ('email') {
                    <input [formControlName]="field.id" [type]="field.type" class="input"
                           [class.is-danger]="form.get(field.id)?.invalid && (form.get(field.id)?.dirty || form.get(field.id)?.touched)">
                  }
                  @case ('date') {
                    <input [formControlName]="field.id" [type]="field.type" class="input"
                           [class.is-danger]="form.get(field.id)?.invalid && (form.get(field.id)?.dirty || form.get(field.id)?.touched)">
                  }
                  @case ('time') {
                    <input [formControlName]="field.id" [type]="field.type" class="input"
                           [class.is-danger]="form.get(field.id)?.invalid && (form.get(field.id)?.dirty || form.get(field.id)?.touched)">
                  }
                  @case ('number') {
                    <input [formControlName]="field.id" [type]="field.type" class="input"
                           [class.is-danger]="form.get(field.id)?.invalid && (form.get(field.id)?.dirty || form.get(field.id)?.touched)">
                  }
                  @case ('files') {
                    <app-file-uploads
                      [fieldName]="field.id"
                      [required]="field.required"
                    />
                  }
                  @case ('radio') {
                    @if (field.options) {
                      @for (option of field.options; track option.value) {
                        <div class="control">
                          <label class="radio">
                            <input type="radio" [formControlName]="field.id" [value]="option.value">
                            {{ option.label }}
                          </label>
                        </div>
                      }
                    }
                  }
                  @case ('autocomplete') {
                    <mat-form-field class="full-width">
                      <mat-label>{{ field.label }}</mat-label>
                      <input matInput
                             [formControlName]="field.id"
                             [matAutocomplete]="auto"
                             (input)="filterAutocomplete(field.id, field.sourceId || '')"
                             (blur)="onAutocompleteBlur(field.id, field.sourceId || '')">
                      <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn">
                        @for (option of filteredOptions[field.id]; track option) {
                          <mat-option [value]="option">{{ option }}</mat-option>
                        }
                        @if (filteredOptions[field.id]?.length === 0) {
                          <mat-option disabled>{{ 'form.autocomplete.noResults' | localize }}</mat-option>
                        }
                      </mat-autocomplete>
                    </mat-form-field>
                  }
                }
              }
            </div>
            @if (field.id && form.get(field.id)?.invalid && (form.get(field.id)?.dirty || form.get(field.id)?.touched)) {
              <p class="help is-danger">
                @for (validation of field.validations; track validation.type) {
                  @if (form.get(field.id)?.hasError(validation.type)) {
                    <span>{{ validation.message }}</span>
                  }
                }
              </p>
            }
          </div>
        }
        <!-- Progress indicator -->
        @if (submissionFlowState() !== 'form' && submissionFlowState() !== 'complete' && submissionFlowState() !== 'error') {
          <div class="notification is-info mt-4">
            @switch (submissionFlowState()) {
              @case ('submitting') {
                <p><strong>{{ 'form.submitting.message' | localize }}</strong></p>
                <progress class="progress is-primary" max="100"></progress>
              }
              @case ('uploading') {
                <p><strong>{{ 'form.uploading.message' | localize }} ({{ uploadProgress.current }}/{{ uploadProgress.total }}): {{ currentUploadField }}</strong></p>
                <progress class="progress is-primary" [value]="uploadProgress.current" [max]="uploadProgress.total"></progress>
              }
              @case ('finalizing') {
                <p><strong>{{ 'form.finalizing.message' | localize }}</strong></p>
                <progress class="progress is-primary" max="100"></progress>
              }
            }
          </div>
        }

        <!-- Error message -->
        @if (submissionFlowState() === 'error' && error) {
          <div class="notification is-danger mt-4">
            <p><strong>{{ 'form.error.prefix' | localize }}:</strong> {{ error }}</p>
            <button type="button" class="button is-small is-danger is-outlined mt-2" (click)="submissionFlowState.set('form')">{{ 'form.error.tryAgain' | localize }}</button>
          </div>
        }

        <div class="field">
          <div class="control">
            <button type="submit"
                    [disabled]="!canSubmit || submissionFlowState() === 'submitting' || submissionFlowState() === 'uploading' || submissionFlowState() === 'finalizing'"
                    class="button is-primary">
              @if (submissionFlowState() === 'submitting' || submissionFlowState() === 'uploading' || submissionFlowState() === 'finalizing') {
                {{ 'form.button.processing' | localize }}
              } @else {
                {{ 'form.button.submit' | localize }}
              }
            </button>
          </div>
        </div>
      </form>
    </div>
  }

  <!-- Completion Page -->
  @if (submissionFlowState() === 'complete') {
    <app-completion-page
      [submissionId]="completionSubmissionId"
      [formTitle]="completionFormTitle"
      [submittedAt]="completionSubmittedAt"
      (submitAnother)="onSubmitAnother()"
    />
  }
}
